import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { addDays, isSameDay } from 'date-fns';
import { cx } from '@emotion/css';
import { useReactiveVar } from '@apollo/client';

import { toDate } from '../../../../../../utils/date/to-date';
import {
  IStepComponentProps,
  IStepperImperativeHandleProps,
  IFormImperativeHandleProps,
  IconTypes,
  IconSizes,
  Icon,
} from '../../../../../common';
import { DateStepForm } from './date-step-form';
import { useStyles } from './date-step.styles';
import { ExternalState, IStepsState } from '../../appointment-registration-modal.models';
import { getDateTitle } from '../../utils';
import { EcpServices } from '../../../ecp-locator';
import { setStoreDetail } from '../../../../../../graphql/ecp-locator/ecp-locator.cache';
import { formatPhoneNumber } from '../../../../../../utils';
import { setCvTerms, setRegStep } from '../../../../../../graphql';

export const DateStep = forwardRef<
IStepperImperativeHandleProps, IStepComponentProps>(({
  onGoNextSuccess,
  stepsState,
  externalState,
  onNextButtonDisabled,
}, ref): JSX.Element => {
  const classes = useStyles();
  const formRef = useRef<IFormImperativeHandleProps>(null);
  const ecpService = (externalState as ExternalState).ecpService;
  const cvTerms = useReactiveVar(setCvTerms);
  const isCvWithAvailableDates = ecpService === EcpServices.cv && !!cvTerms.startDate && !!cvTerms.endDate;
  const tomorrow = useMemo(() => addDays(toDate(new Date()) as Date, 1), []);
  const selectedCvDate = useMemo(() => {
    if (ecpService === EcpServices.cv
      && !!cvTerms.startDate
      && tomorrow >= (toDate(cvTerms.startDate) as Date)
      && !!cvTerms.endDate
      && tomorrow <= (toDate(cvTerms.endDate) as Date)) {
      return tomorrow;
    }
    return new Date(cvTerms.startDate);
  }, [ecpService, tomorrow, cvTerms.startDate, cvTerms.endDate]);
  const minDate = useMemo(() => toDate(
    isCvWithAvailableDates ? cvTerms.startDate : (addDays(new Date(), 2)).toDateString(),
  ), [isCvWithAvailableDates, cvTerms.startDate])!;
  const maxDate = useMemo(() => toDate(
    isCvWithAvailableDates ? cvTerms.endDate : (addDays(new Date(), 30)).toDateString(),
  ), [isCvWithAvailableDates, cvTerms.endDate])!;
  const date = (stepsState as IStepsState).appointmentDate?.date
    ?? (isCvWithAvailableDates ? selectedCvDate : minDate);
  const [selectedDate, setSelectedDate] = useState<Date>(date);
  const isCurrentDateSelected = useMemo(() => isSameDay(selectedDate, new Date()), [selectedDate]);
  const defaultValues = useMemo(() => ({ date }), [date]);
  const titleSettings = useMemo(() => {
    return getDateTitle(ecpService);
  }, [(externalState as ExternalState).ecpService]);
  const storeInfo = useReactiveVar(setStoreDetail);
  const phone = formatPhoneNumber(storeInfo?.phonenumber);

  useEffect((): void => {
    setRegStep(1);
  }, [setRegStep]);

  useEffect(() => {
    setSelectedDate(date);
  }, [date]);

  useEffect(() => {
    onNextButtonDisabled(isCurrentDateSelected);
  }, [isCurrentDateSelected, onNextButtonDisabled]);

  useImperativeHandle(ref, () => ({
    goNext() {
      formRef.current!.submit();
    },
    goBack() {},
  }));

  return (
    <>
      <div
        className={classes.colored}
        data-testid="appointment-date-step-title"
        style={{ backgroundColor: titleSettings.color }}
      >
        <h2 className={cx(classes.title, 'title-medium')}>
          <FormattedMessage id={titleSettings.title} values={{ noBreak: <>&nbsp;</> }} />
        </h2>
        {ecpService === EcpServices.cv && (
          <p className={cx(classes.cvDates, classes.message)}>
            <FormattedMessage
              id="appointmentRegistration.dateStep.cvDates"
              values={{ toDays: cvTerms.toDays, fromDays: cvTerms.fromDays }}
            />
            <Icon className={classes.icon} type={IconTypes.info} size={IconSizes.xs} aria-hidden="true" />
          </p>
        )}
        {ecpService === EcpServices.cv && isCurrentDateSelected && (
          <p className={cx(classes.byPhone, classes.message)}>
            <FormattedMessage id="appointmentRegistration.dateStep.byPhone" values={{ noBreak: <>&nbsp;</> }} />
            <a className={classes.phone} href={`tel:${phone}`}>{phone}</a>
            <Icon className={classes.icon} type={IconTypes.phoneBlack} size={IconSizes.xs} aria-hidden="true" />
          </p>
        )}
      </div>
      <div className={classes.content}>
        <DateStepForm
          ref={formRef}
          minDate={minDate}
          maxDate={maxDate}
          defaultValues={defaultValues}
          onSubmit={onGoNextSuccess}
          selectedColor={titleSettings.color}
          onDateChange={setSelectedDate}
        />
      </div>
    </>
  );
});
